/**
 * Server-side pose history for lag compensation (ADR-0002: a ring of mech transforms, not
 * physics snapshots). The room records both poses every tick and rewinds a shooter's target to
 * the fractional tick the shooter saw, clamped to the 500 ms window.
 */
import { wrapAngle } from '../sim/math.js';
import { NET } from './constants.js';
function lerpAngle(a, b, k) {
    return wrapAngle(a + wrapAngle(b - a) * k);
}
function copyPose(p) {
    return { position: { ...p.position }, yaw: p.yaw, aimYaw: p.aimYaw, aimPitch: p.aimPitch };
}
/** Linear blend of two poses (shortest arc for the angles). */
export function lerpPose(a, b, k) {
    if (k <= 0)
        return copyPose(a);
    if (k >= 1)
        return copyPose(b);
    return {
        position: {
            x: a.position.x + (b.position.x - a.position.x) * k,
            y: a.position.y + (b.position.y - a.position.y) * k,
            z: a.position.z + (b.position.z - a.position.z) * k,
        },
        yaw: lerpAngle(a.yaw, b.yaw, k),
        aimYaw: lerpAngle(a.aimYaw, b.aimYaw, k),
        aimPitch: a.aimPitch + (b.aimPitch - a.aimPitch) * k,
    };
}
export class PoseHistory {
    capacity;
    entries = [];
    constructor(capacity = NET.HISTORY_TICKS + 1) {
        this.capacity = capacity;
    }
    get size() {
        return this.entries.length;
    }
    oldestTick() {
        return this.entries[0]?.tick ?? null;
    }
    newestTick() {
        return this.entries[this.entries.length - 1]?.tick ?? null;
    }
    /** Records the poses at `tick` (ticks must arrive in increasing order). */
    record(tick, poses) {
        const last = this.entries[this.entries.length - 1];
        if (last && tick <= last.tick)
            return;
        this.entries.push({ tick, poses: [copyPose(poses[0]), copyPose(poses[1])] });
        while (this.entries.length > this.capacity)
            this.entries.shift();
    }
    clear() {
        this.entries.length = 0;
    }
    /**
     * The pose of `slot` at a fractional tick, blended between the two recorded neighbours and
     * clamped to the window; null when nothing has been recorded yet.
     */
    at(slot, tick) {
        const n = this.entries.length;
        if (n === 0)
            return null;
        const first = this.entries[0];
        const last = this.entries[n - 1];
        if (tick <= first.tick)
            return copyPose(first.poses[slot]);
        if (tick >= last.tick)
            return copyPose(last.poses[slot]);
        // Binary search for the entry at or before `tick`.
        let lo = 0;
        let hi = n - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (this.entries[mid].tick <= tick)
                lo = mid;
            else
                hi = mid;
        }
        const a = this.entries[lo];
        const b = this.entries[hi];
        const span = b.tick - a.tick;
        const k = span > 0 ? (tick - a.tick) / span : 0;
        return lerpPose(a.poses[slot], b.poses[slot], k);
    }
}
//# sourceMappingURL=history.js.map